import { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Search, Network, ArrowRight, ArrowLeft, Loader2, 
  Users, Activity, TrendingUp, ExternalLink, Copy, Check
} from 'lucide-react';
import { Link } from 'react-router-dom';
import HolderDistributionChart from '../components/HolderDistributionChart';
import MarketPressureGauge from '../components/MarketPressureGauge';
import VolumeHeatmap from '../components/VolumeHeatmap';
import TopTradersTable from '../components/TopTradersTable';
import { useChain } from '../context/ChainContext';
import McTokenAnalytics from '../components/McTokenAnalytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AnalyticsPage() {
  const { chain, chainParam, chainInfo } = useChain();
  const isKrown = !chainParam || chain === 'krown';
  const [searchAddress, setSearchAddress] = useState('');
  const [walletData, setWalletData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const searchWalletConnections = useCallback(async (address) => {
    if (!address || address.length < 42) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/analytics/wallet-connections?address=${address}&depth=1`);
      if (!response.ok) throw new Error('Failed to fetch wallet connections');
      const data = await response.json();
      setWalletData(data);
    } catch (err) {
      setError(err.message);
      setWalletData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    searchWalletConnections(searchAddress);
  };

  const copyAddress = (addr) => {
    navigator.clipboard.writeText(addr);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatAddress = (addr) => {
    if (!addr) return '';
    return `${addr.slice(0, 8)}...${addr.slice(-6)}`;
  };

  const formatAmount = (amount, token) => {
    if (amount >= 1000000) return `${(amount / 1000000).toFixed(2)}M ${token}`;
    if (amount >= 1000) return `${(amount / 1000).toFixed(2)}K ${token}`;
    return `${amount.toFixed(2)} ${token}`;
  };

  const getEdgeColor = (type) => {
    switch(type) {
      case 'transfer_out':
      case 'native_out':
        return 'text-red-500';
      case 'transfer_in':
      case 'native_in':
        return 'text-emerald-500';
      default:
        return 'text-muted-foreground';
    }
  };

  return (
    <div className="container mx-auto p-4 space-y-6" data-testid="analytics-page">
      {/* Header */}
      <div className="flex flex-col gap-2">
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <Activity className="w-8 h-8 text-primary" />
          Analytics &amp; Forensics
        </h1>
        <p className="text-muted-foreground" data-testid="analytics-chain-subtitle">
          {isKrown
            ? 'Krown internal-indexer forensics — holder distribution, market pressure, top traders & wallet flows'
            : <>Live market analytics & recent-trade forensics on <span className="text-foreground font-medium">{chainInfo?.label || chain}</span></>}
        </p>
      </div>

      {/* Non-Krown chains: public-data analytics (DexScreener + GeckoTerminal) */}
      {!isKrown && <McTokenAnalytics key={chain} chain={chain} chainInfo={chainInfo} />}

      {/* Analytics Dashboard (Krown internal indexer) */}
      {isKrown && (
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid w-full grid-cols-3 lg:w-[400px]">
          <TabsTrigger value="overview" className="flex items-center gap-1">
            <TrendingUp className="w-4 h-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="traders" className="flex items-center gap-1">
            <Users className="w-4 h-4" />
            Traders
          </TabsTrigger>
          <TabsTrigger value="forensics" className="flex items-center gap-1">
            <Network className="w-4 h-4" />
            Forensics
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <MarketPressureGauge hours={24} />
            <HolderDistributionChart />
            <VolumeHeatmap days={7} />
          </div>
        </TabsContent>

        {/* Traders Tab */}
        <TabsContent value="traders" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <TopTradersTable limit={15} />
            <div className="space-y-6">
              <MarketPressureGauge hours={24} />
              <VolumeHeatmap days={7} />
            </div>
          </div>
        </TabsContent>

        {/* Forensics Tab */}
        <TabsContent value="forensics" className="space-y-6 mt-6">
          {/* Wallet Search */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Network className="w-5 h-5" />
                Wallet Relationship Mapper
              </CardTitle>
              <CardDescription>
                Analyze wallet connections and trace token flows between addresses
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSearch} className="flex gap-2">
                <Input
                  placeholder="Enter wallet address (0x...)"
                  value={searchAddress}
                  onChange={(e) => setSearchAddress(e.target.value)}
                  className="font-mono"
                />
                <Button type="submit" disabled={loading || searchAddress.length < 42}>
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Search className="w-4 h-4" />
                  )}
                  <span className="ml-2">Analyze</span>
                </Button>
              </form>

              {error && (
                <div className="mt-4 p-3 rounded-lg bg-red-500/10 text-red-500 text-sm">
                  {error}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Wallet Connections Result */}
          {walletData && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Network className="w-5 h-5" />
                    Connections for {formatAddress(walletData.target_address)}
                  </span>
                  <Badge variant="outline">
                    {walletData.stats.total_connections} connections
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Stats Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-center">
                    <div className="text-2xl font-bold text-emerald-500">
                      {walletData.stats.incoming_transfers + walletData.stats.native_in}
                    </div>
                    <div className="text-xs text-muted-foreground">Incoming</div>
                  </div>
                  <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-center">
                    <div className="text-2xl font-bold text-red-500">
                      {walletData.stats.outgoing_transfers + walletData.stats.native_out}
                    </div>
                    <div className="text-xs text-muted-foreground">Outgoing</div>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/50 text-center">
                    <div className="text-2xl font-bold">{walletData.nodes.length - 1}</div>
                    <div className="text-xs text-muted-foreground">Unique Wallets</div>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/50 text-center">
                    <div className="text-2xl font-bold">{walletData.edges.length}</div>
                    <div className="text-xs text-muted-foreground">Connections</div>
                  </div>
                </div>

                {/* Connection List */}
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  <h4 className="font-semibold text-sm text-muted-foreground mb-3">
                    Token Flows
                  </h4>
                  
                  {walletData.edges.map((edge, i) => (
                    <div 
                      key={i}
                      className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                    >
                      {/* From address */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          {edge.from === walletData.target_address ? (
                            <Badge className="bg-purple-500/20 text-purple-500">TARGET</Badge>
                          ) : (
                            <Link 
                              to={`/address/${edge.from}`}
                              className="font-mono text-sm hover:text-primary flex items-center gap-1"
                            >
                              {formatAddress(edge.from)}
                              <ExternalLink className="w-3 h-3" />
                            </Link>
                          )}
                        </div>
                      </div>

                      {/* Arrow and amount */}
                      <div className={`flex items-center gap-2 ${getEdgeColor(edge.type)}`}>
                        {edge.type.includes('out') ? (
                          <ArrowRight className="w-4 h-4" />
                        ) : (
                          <ArrowLeft className="w-4 h-4" />
                        )}
                        <div className="text-center min-w-[100px]">
                          <div className="text-sm font-medium">
                            {formatAmount(edge.amount, edge.token)}
                          </div>
                          <div className="text-xs opacity-70">
                            {edge.count} txns
                          </div>
                        </div>
                        {edge.type.includes('out') ? (
                          <ArrowRight className="w-4 h-4" />
                        ) : (
                          <ArrowLeft className="w-4 h-4" />
                        )}
                      </div>

                      {/* To address */}
                      <div className="flex-1 min-w-0 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {edge.to === walletData.target_address ? (
                            <Badge className="bg-purple-500/20 text-purple-500">TARGET</Badge>
                          ) : (
                            <Link 
                              to={`/address/${edge.to}`}
                              className="font-mono text-sm hover:text-primary flex items-center gap-1"
                            >
                              {formatAddress(edge.to)}
                              <ExternalLink className="w-3 h-3" />
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}

                  {walletData.edges.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      No connections found for this wallet
                    </div>
                  )}
                </div>

                {/* View Full Analysis Button */}
                <div className="mt-4 pt-4 border-t">
                  <Link to={`/address/${walletData.target_address}`}>
                    <Button variant="outline" className="w-full">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      View Full Wallet Analysis
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Forensics Tools Placeholder */}
          {!walletData && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="border-dashed">
                <CardHeader>
                  <CardTitle className="text-lg">Transaction Tracer</CardTitle>
                  <CardDescription>
                    Follow the flow of tokens across multiple hops
                  </CardDescription>
                </CardHeader>
                <CardContent className="text-center py-8 text-muted-foreground">
                  Enter a wallet address above to start tracing
                </CardContent>
              </Card>

              <Card className="border-dashed">
                <CardHeader>
                  <CardTitle className="text-lg">Cluster Analysis</CardTitle>
                  <CardDescription>
                    Identify related wallets and potential whale clusters
                  </CardDescription>
                </CardHeader>
                <CardContent className="text-center py-8 text-muted-foreground">
                  Coming soon - analyze wallet patterns
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>
      )}
    </div>
  );
}
